/* istanbul ignore file */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { AiOutlineDelete } from 'react-icons/ai';
import { FiEdit2, FiSend } from 'react-icons/fi';
import { FaCircle, FaPlus } from 'react-icons/fa';
import { useNavigate, Navigate } from 'react-router-dom';
import '../styles/users_admin_list_view.css';

import { AdminView } from './user_creation_form';
import { sendUserActivationEmail, getUsersWithRoles, deleteUser } from '../services/admin_service';
import { useAuth, useAdminAccess, useUserRole } from '../contexts/auth_context';

// ─── Constants ────────────────────────────────────────────────────────────────

const COOLDOWN_SECONDS = 60;
const TOAST_DURATION_MS = 3000;
const TOAST_LONG_DURATION_MS = 5000;

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function canModifyUser(userRole) {
	return function (targetUserRole) {
		if (!userRole) return false;
		// Superusers may edit and demote other superusers, so the privilege is
		// reversible. The server refuses to remove the last active one, and refuses
		// to let anyone change their own role, so neither is possible by accident.
		if (userRole === 'superuser') return true;
		if (userRole === 'admin')
			return targetUserRole === 'client' || targetUserRole === 'consultant';
		if (userRole === 'consultant') return targetUserRole === 'client';
		return false;
	};
}

const setErrorString = setError => err => {
	if (!err) return setError('');
	if (typeof err === 'string') return setError(err);
	if (err instanceof Error && err.message) return setError(err.message);
	if (typeof err === 'object' && Object.keys(err).length === 0) return setError('');
	try {
		return setError(JSON.stringify(err));
	} catch {
		return setError('An unknown error occurred');
	}
};

// ─── Role badge ───────────────────────────────────────────────────────────────

const ROLE_STYLES = {
	superuser: { bg: 'rgba(255,11,120,0.12)', color: '#FF0B78', border: 'rgba(255,11,120,0.3)' },
	admin: { bg: 'rgba(1,141,187,0.12)', color: '#018DBB', border: 'rgba(1,141,187,0.3)' },
	consultant: { bg: 'rgba(245,158,11,0.12)', color: '#F59E0B', border: 'rgba(245,158,11,0.3)' },
	client: { bg: 'rgba(34,197,94,0.12)', color: '#22C55E', border: 'rgba(34,197,94,0.3)' },
};

const RoleBadge = ({ role }) => {
	const s = ROLE_STYLES[role] ?? {
		bg: 'var(--app-surface-muted)',
		color: 'var(--app-text-muted)',
		border: 'var(--app-border)',
	};
	return (
		<span
			className='inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full border capitalize'
			style={{ background: s.bg, color: s.color, borderColor: s.border }}
		>
			{role}
		</span>
	);
};

// ─── UserAdminView ────────────────────────────────────────────────────────────

const UserAdminView = () => {
	const { user } = useAuth();
	const { hasAdminAccess, isLoadingAccess } = useAdminAccess();
	const { isLoadingRole } = useUserRole();
	const navigate = useNavigate();

	const [showAdminForm, setShowAdminForm] = useState(false);
	const [users, setUsers] = useState([]);
	const [error, setError] = useState('');
	const [toast, setToast] = useState({ message: '', type: 'success', visible: false });
	const [busyIds, setBusyIds] = useState(() => new Set());
	const [cooldowns, setCooldowns] = useState([]);
	const [deleteConfirm, setDeleteConfirm] = useState(null);
	// eslint-disable-next-line no-unused-vars
	const [_tick, setTick] = useState(0);
	const [query, setQuery] = useState('');
	const setErrorSafe = setErrorString(setError);
	const popoverRef = useRef(null);

	const formatDateUK = value => {
		if (!value) return '—';
		const d = new Date(value);
		if (Number.isNaN(d.getTime())) return value;
		return d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
	};

	// ── Data ──────────────────────────────────────────────────────────────────

	const fetchUsers = useCallback(async () => {
		try {
			const response = await getUsersWithRoles();
			setUsers(Array.isArray(response.users) ? response.users : []);
		} catch {
			setUsers([]);
		}
	}, []);

	useEffect(() => {
		fetchUsers();
	}, [fetchUsers]);
	useEffect(() => {
		const id = setInterval(() => setTick(t => t + 1), 1000);
		return () => clearInterval(id);
	}, []);

	// Close popover on outside click / Escape
	useEffect(() => {
		if (!showAdminForm) return undefined;
		const onDocClick = e => {
			if (popoverRef.current && !popoverRef.current.contains(e.target))
				setShowAdminForm(false);
		};
		const onEsc = e => {
			if (e.key === 'Escape') setShowAdminForm(false);
		};
		document.addEventListener('mousedown', onDocClick);
		document.addEventListener('keydown', onEsc);
		return () => {
			document.removeEventListener('mousedown', onDocClick);
			document.removeEventListener('keydown', onEsc);
		};
	}, [showAdminForm]);

	// ── Actions ───────────────────────────────────────────────────────────────

	const handleDeleteUser = async () => {
		if (!deleteConfirm) return;
		const { id } = deleteConfirm;
		setDeleteConfirm(null);
		try {
			await deleteUser(id);
			await fetchUsers();
		} catch {
			/* silent */
		}
	};

	const showToast = useCallback((message, type = 'success') => {
		setToast({ message, type, visible: true });
		setTimeout(() => setToast(prev => ({ ...prev, visible: false })), TOAST_DURATION_MS);
	}, []);

	const getRemainingSeconds = useCallback(
		userId => {
			const entry = cooldowns.find(c => c.id === userId);
			if (!entry) return 0;
			const remaining = Math.ceil((entry.until - Date.now()) / 1000);
			return remaining > 0 ? remaining : 0;
		},
		[cooldowns]
	);

	const handleResendInvite = useCallback(
		async (userId, email, role) => {
			if (!email || !role) {
				showToast('Cannot resend invite: missing email or role', 'error');
				return;
			}
			setBusyIds(prev => new Set([...prev, userId]));
			try {
				const result = await sendUserActivationEmail(email, role);
				if (result?.emailSent) showToast('Activation email sent successfully');
				else if (result?.emailError === 'RATE_LIMITED')
					showToast(
						'Too many requests. Try again later.',
						'error',
						TOAST_LONG_DURATION_MS
					);
				else if (result?.emailError === 'SMTP_NOT_CONFIGURED')
					showToast('SMTP not configured. User must request from profile.', 'error');
				else if (result?.message) showToast(result.message, 'info', TOAST_LONG_DURATION_MS);
				else showToast('Email sent but delivery status unknown');
				setBusyIds(prev => {
					const next = new Set(prev);
					next.delete(userId);
					return next;
				});
				setCooldowns(prev => [
					...prev.filter(c => c.id !== userId),
					{ id: userId, until: Date.now() + COOLDOWN_SECONDS * 1000 },
				]);
			} catch {
				showToast('Failed to send invite. Please try again.', 'error');
				setBusyIds(prev => {
					const next = new Set(prev);
					next.delete(userId);
					return next;
				});
			}
		},
		[showToast]
	);

	// ── Filtered + sorted list ────────────────────────────────────────────────

	const filteredUsers = useMemo(() => {
		const q = (query || '').trim().toLowerCase();
		const list = q
			? users.filter(
					u =>
						`${u.first_name || ''} ${u.last_name || ''}`.toLowerCase().includes(q) ||
						String(u.id || '')
							.toLowerCase()
							.includes(q)
				)
			: [...users];

		const roleOrder = { superuser: 1, admin: 2, consultant: 3, client: 4 };
		list.sort((a, b) => {
			if (a.id === user?.uid) return -1;
			if (b.id === user?.uid) return 1;
			return (roleOrder[a.role] ?? 5) - (roleOrder[b.role] ?? 5);
		});
		return list;
	}, [users, query, user?.uid]);

	// ── Guards ────────────────────────────────────────────────────────────────

	if (isLoadingAccess || isLoadingRole) {
		return (
			<div className='flex items-center justify-center min-h-[50vh]'>
				<div
					className='w-8 h-8 rounded-full border-2 animate-spin'
					style={{ borderColor: 'var(--app-border)', borderTopColor: '#FF0B78' }}
				/>
			</div>
		);
	}
	if (!hasAdminAccess) return <Navigate to='/' replace />;

	// ── Render ────────────────────────────────────────────────────────────────

	return (
		<>
			{/* Toast */}
			{toast.visible && (
				<div
					className='fixed top-4 right-4 z-50 px-4 py-2.5 rounded-xl text-[13px] font-semibold text-white shadow-lg'
					style={{
						background:
							toast.type === 'error'
								? 'rgba(239,68,68,0.95)'
								: 'rgba(34,197,94,0.95)',
					}}
					role='status'
					aria-live='polite'
				>
					{toast.message}
				</div>
			)}

			<div
				className='min-h-[calc(100vh-96px)] p-6 md:p-8 pt-5 md:pt-6'
				style={{ color: 'var(--app-text)' }}
			>
				{/* ── Toolbar ───────────────────────────────────────────────── */}
				<div className='flex items-center justify-between gap-4 mb-5 flex-wrap'>
					<p className='text-[12px]' style={{ color: 'var(--app-text-muted)' }}>
						{filteredUsers.length} of {users.length} users
					</p>
					<div className='flex items-center gap-3'>
						<input
							placeholder='Search name or ID…'
							value={query}
							onChange={e => setQuery(e.target.value)}
							aria-label='Search users by name or id'
							className='text-[13px] px-3 py-2 rounded-lg'
							style={{
								background: 'var(--app-input-bg)',
								border: '1px solid var(--app-border)',
								color: 'var(--app-text)',
								outline: 'none',
								width: 240,
								fontFamily: 'inherit',
							}}
							onFocus={e => (e.target.style.borderColor = '#FF0B78')}
							onBlur={e => (e.target.style.borderColor = 'var(--app-border)')}
						/>

						{/* Add user button + popover */}
						<div className='user-admin-add-anchor'>
							<button
								onClick={() => {
									setShowAdminForm(s => !s);
									setError('');
								}}
								className='flex items-center gap-2 px-4 py-2 rounded-xl text-[12px] font-semibold text-white transition-opacity hover:opacity-80'
								style={{ background: 'linear-gradient(90deg, #FF0B78, #E300C9)' }}
							>
								<FaPlus size={10} /> Add user
							</button>

							{showAdminForm && (
								<div
									className='user-admin-popover'
									ref={popoverRef}
									role='dialog'
									aria-modal='true'
								>
									<button
										className='user-admin-popover-close'
										aria-label='Close'
										onClick={() => {
											setShowAdminForm(false);
											setError('');
										}}
									>
										×
									</button>
									<AdminView
										onCreateUser={async (email, role) => {
											try {
												setErrorSafe('');
												const result = await sendUserActivationEmail(
													email,
													role
												);
												setShowAdminForm(false);
												if (result?.emailSent) {
													showToast(
														'User created and invite sent successfully'
													);
												} else if (
													result?.emailError === 'SMTP_NOT_CONFIGURED'
												) {
													showToast(
														'User created. SMTP not configured — user must request activation from their profile.',
														'error'
													);
												} else if (result?.emailError === 'RATE_LIMITED') {
													showToast(
														'Too many requests. Try again later.',
														'error'
													);
												} else {
													showToast(
														'User created but activation email failed. They can request it from their profile.',
														'error'
													);
												}
											} catch (err) {
												setError(
													typeof err === 'string'
														? err
														: err?.message ||
																JSON.stringify(err) ||
																'Failed to create user'
												);
											}
										}}
										asPopover
										error={error}
									/>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* ── Table ─────────────────────────────────────────────────── */}
				<div
					className='rounded-xl border overflow-hidden'
					style={{
						background: 'var(--app-surface)',
						borderColor: 'var(--app-border)',
						maxHeight: 'calc(100vh - 200px)',
						overflowY: 'auto',
					}}
				>
					{/* Column headers */}
					<div
						className='ua-grid text-[11px] font-bold uppercase tracking-wider px-5 py-3 sticky top-0 z-10'
						style={{
							background: 'var(--app-surface-muted)',
							borderBottom: '1px solid var(--app-border)',
							color: 'var(--app-text-muted)',
						}}
					>
						<span>Name</span>
						<span>ID</span>
						<span className='flex justify-center'>Role</span>
						<span className='flex justify-center'>2FA</span>
						<span>Last Activity</span>
						<span className='flex justify-center'>Active</span>
						<span>Actions</span>
					</div>

					{/* Rows */}
					{filteredUsers.length === 0 ? (
						<div
							className='flex items-center justify-center py-16 text-[13px]'
							style={{ color: 'var(--app-text-muted)' }}
						>
							No users found.
						</div>
					) : (
						filteredUsers.map(tableUser => {
							const isCurrent = tableUser.id === user?.uid;
							return (
								<div
									key={tableUser.id}
									className='ua-grid ua-row px-5 py-3 text-[13px] relative'
									style={{
										borderBottom: '1px solid var(--app-border)',
										background: isCurrent
											? 'rgba(255,11,120,0.04)'
											: 'transparent',
										borderLeft: isCurrent
											? '3px solid #FF0B78'
											: '3px solid transparent',
									}}
								>
									{/* Name */}
									<span
										className='font-medium truncate'
										style={{ color: 'var(--app-text)' }}
									>
										{tableUser.first_name} {tableUser.last_name}
										{isCurrent && (
											<span
												className='ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full'
												style={{
													background: 'rgba(255,11,120,0.12)',
													color: '#FF0B78',
												}}
											>
												You
											</span>
										)}
									</span>

									{/* ID */}
									<span
										className='truncate font-mono text-[11px]'
										style={{ color: 'var(--app-text-muted)' }}
									>
										{tableUser.id}
									</span>

									{/* Role */}
									<span className='flex justify-center'>
										<RoleBadge role={tableUser.role} />
									</span>

									{/* 2FA */}
									<span className='flex justify-center'>
										{tableUser.two_factor_enabled ? (
											<span
												className='text-[10px] font-bold px-2 py-0.5 rounded-full border'
												style={{
													background: 'rgba(34,197,94,0.12)',
													color: '#22C55E',
													borderColor: 'rgba(34,197,94,0.3)',
												}}
											>
												On
											</span>
										) : (
											<span
												className='text-[10px] font-bold px-2 py-0.5 rounded-full border'
												style={{
													background: 'var(--app-surface-muted)',
													color: 'var(--app-text-muted)',
													borderColor: 'var(--app-border)',
												}}
											>
												Off
											</span>
										)}
									</span>

									{/* Last activity */}
									<span style={{ color: 'var(--app-text-muted)' }}>
										{formatDateUK(tableUser.last_login_at)}
									</span>

									{/* Active status */}
									<span className='flex justify-center'>
										<button
											type='button'
											className='user-admin-status-wrapper'
											data-tooltip={
												tableUser.is_active ? 'Active' : 'Inactive'
											}
											aria-label={tableUser.is_active ? 'Active' : 'Inactive'}
										>
											<FaCircle
												size={9}
												style={{
													color: tableUser.is_active
														? '#22C55E'
														: '#EF4444',
												}}
											/>
										</button>
									</span>

									{/* Actions */}
									<span className='flex items-center gap-2'>
										<button
											type='button'
											onClick={() =>
												navigate('/admin/permissions', {
													state: {
														userId: tableUser.id,
														user: tableUser,
													},
												})
											}
											className='ua-action-btn'
											data-tooltip='Edit'
											aria-label='Edit user'
											onMouseEnter={e =>
												(e.currentTarget.style.color = '#018DBB')
											}
											onMouseLeave={e =>
												(e.currentTarget.style.color =
													'var(--app-text-muted)')
											}
										>
											<FiEdit2 size={14} />
										</button>
										{!isCurrent && (
											<button
												type='button'
												onClick={() =>
													setDeleteConfirm({
														id: tableUser.id,
														name:
															tableUser.first_name &&
															tableUser.last_name
																? `${tableUser.first_name} ${tableUser.last_name}`
																: tableUser.email,
													})
												}
												className='ua-action-btn'
												data-tooltip='Delete'
												aria-label='Delete user'
												onMouseEnter={e =>
													(e.currentTarget.style.color = '#FF0B78')
												}
												onMouseLeave={e =>
													(e.currentTarget.style.color =
														'var(--app-text-muted)')
												}
											>
												<AiOutlineDelete size={15} />
											</button>
										)}
										{!isCurrent && tableUser.email && (
											<button
												type='button'
												onClick={() =>
													handleResendInvite(
														tableUser.id,
														tableUser.email,
														tableUser.role
													)
												}
												className='ua-action-btn'
												data-tooltip={
													getRemainingSeconds(tableUser.id) > 0
														? `Retry in ${getRemainingSeconds(tableUser.id)}s`
														: 'Resend invite'
												}
												aria-label='Resend invite'
												disabled={
													busyIds.has(tableUser.id) ||
													getRemainingSeconds(tableUser.id) > 0
												}
												style={{
													opacity:
														busyIds.has(tableUser.id) ||
														getRemainingSeconds(tableUser.id) > 0
															? 0.4
															: 1,
												}}
												onMouseEnter={e =>
													(e.currentTarget.style.color = '#F59E0B')
												}
												onMouseLeave={e =>
													(e.currentTarget.style.color =
														'var(--app-text-muted)')
												}
											>
												<FiSend size={13} />
											</button>
										)}
									</span>
								</div>
							);
						})
					)}
				</div>
			</div>

			{deleteConfirm && (
				<div
					className='fixed inset-0 z-50 flex items-center justify-center'
					style={{ background: 'rgba(0,0,0,0.45)' }}
				>
					<button
						type='button'
						aria-label='Close modal'
						className='absolute inset-0 w-full h-full border-0 cursor-default'
						style={{ background: 'transparent' }}
						onClick={() => setDeleteConfirm(null)}
					/>
					<div
						role='dialog'
						aria-modal='true'
						aria-labelledby='delete-confirm-title'
						className='relative rounded-xl p-6 w-full max-w-sm mx-4 shadow-xl z-10'
						style={{
							background: 'var(--app-surface)',
							border: '1px solid var(--app-border)',
						}}
					>
						<h3
							id='delete-confirm-title'
							className='text-[15px] font-semibold mb-2'
							style={{ color: 'var(--app-text)' }}
						>
							Delete user
						</h3>
						<p className='text-[13px] mb-6' style={{ color: 'var(--app-text-muted)' }}>
							Are you sure you want to delete{' '}
							<span style={{ color: 'var(--app-text)', fontWeight: 600 }}>
								{deleteConfirm.name}
							</span>
							? This action cannot be undone.
						</p>
						<div className='flex justify-end gap-3'>
							<button
								type='button'
								onClick={() => setDeleteConfirm(null)}
								className='px-4 py-1.5 rounded-lg text-[13px] font-medium'
								style={{
									background: 'var(--app-surface-muted)',
									color: 'var(--app-text)',
									border: '1px solid var(--app-border)',
								}}
							>
								Cancel
							</button>
							<button
								type='button'
								onClick={handleDeleteUser}
								className='px-4 py-1.5 rounded-lg text-[13px] font-medium text-white'
								style={{ background: '#FF0B78', border: 'none' }}
							>
								Delete
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export const UserAdmin = React.memo(UserAdminView);
